import { morph } from './morph.js';

/**
 * A class to re-render sections using the Section Rendering API
 * with intelligent caching and morphing for smooth updates
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL|string} [options.url] - The URL to render the section from
   * @param {Object} [options.morphCallbacks] - Callbacks for morph operations
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url, morphCallbacks } = options ?? {};

    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        this.#abortControllersBySectionId.delete(sectionId);
        morphSection(sectionId, sectionHTML, morphCallbacks);
      }

      return sectionHTML;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Section rendering error:', error);
      }
      throw error;
    }
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL|string} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = window.location.href) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    try {
      const sectionHTML = await pendingPromise;
      this.#pendingPromises.delete(sectionUrl);
      this.#cache.set(sectionUrl, sectionHTML);
      return sectionHTML;
    } catch (error) {
      this.#pendingPromises.delete(sectionUrl);
      throw error;
    }
  }

  /**
   * Clears the cache for a specific section or all sections
   * @param {string} [sectionId] - The section ID (optional)
   */
  clearCache(sectionId = null) {
    if (sectionId) {
      // Clear cache for specific section
      for (const [key] of this.#cache) {
        if (key.includes(normalizeSectionId(sectionId))) {
          this.#cache.delete(key);
        }
      }
    } else {
      // Clear all cache
      this.#cache.clear();
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL|string} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = window.location.href) {
  const urlObject = typeof url === 'string' ? new URL(url) : new URL(url.toString());
  urlObject.searchParams.set('section_id', normalizeSectionId(sectionId));
  urlObject.searchParams.sort();

  return urlObject.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * Morphs the existing section element with the new section contents
 * Uses View Transition API when available for smooth animations
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [callbacks] - Optional callbacks for morph operations
 */
export async function morphSection(sectionId, html, callbacks = {}) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  // Merge default callbacks with provided callbacks
  const morphCallbacks = {
    beforeNodeMorphed: (oldNode, newNode) => {
      callbacks.beforeNodeMorphed?.(oldNode, newNode);
    },
    afterNodeMorphed: (oldNode, newNode) => {
      callbacks.afterNodeMorphed?.(oldNode, newNode);

      // Re-initialize any Shopify features
      window?.Shopify?.PaymentButton?.init();
      window?.ProductModel?.loadShopifyXR();
    },
    beforeNodeAdded: (node) => {
      callbacks.beforeNodeAdded?.(node);
    },
    afterNodeAdded: (node) => {
      callbacks.afterNodeAdded?.(node);
    },
    beforeNodeRemoved: (node) => {
      callbacks.beforeNodeRemoved?.(node);
    }
  };

  // Use View Transition API if available for smooth animations
  // This enables the same smooth transitions as browser navigation
  if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const transition = document.startViewTransition(() => {
      morph(existingElement, newElement, { callbacks: morphCallbacks });
    });

    // Wait for transition to complete
    try {
      await transition.finished;
    } catch (error) {
      // Transition was skipped or interrupted, but morph completed
      console.debug('View transition interrupted:', error);
    }
  } else {
    // Fallback: morph without View Transition API
    morph(existingElement, newElement, { callbacks: morphCallbacks });
  }
}

// Create and export singleton instance
export const sectionRenderer = new SectionRenderer();

// Make it globally available
window.SectionRenderer = sectionRenderer;
